import { useCallback, useEffect, useMemo, useState } from "react";

export function useSpeechSynthesis(enabled: boolean) {
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [speaking, setSpeaking] = useState(false);
  const supported = "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;

  useEffect(() => {
    if (!supported) return;
    const refresh = () => setVoices(window.speechSynthesis.getVoices());
    refresh();
    window.speechSynthesis.addEventListener("voiceschanged", refresh);
    return () => window.speechSynthesis.removeEventListener("voiceschanged", refresh);
  }, [supported]);

  const frenchVoice = useMemo(
    () =>
      voices.find((voice) => voice.lang.toLowerCase() === "fr-fr") ||
      voices.find((voice) => voice.lang.toLowerCase().startsWith("fr")),
    [voices]
  );

  const cancel = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }, [supported]);

  const speak = useCallback(
    (text: string) => {
      if (!enabled || !supported || !text.trim()) return;
      cancel();
      const utterance = new SpeechSynthesisUtterance(text.slice(0, 900));
      utterance.lang = frenchVoice?.lang || "fr-FR";
      utterance.voice = frenchVoice || null;
      utterance.rate = 1.02;
      utterance.pitch = 1;
      utterance.onstart = () => setSpeaking(true);
      utterance.onend = () => setSpeaking(false);
      utterance.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(utterance);
    },
    [cancel, enabled, frenchVoice, supported]
  );

  useEffect(() => cancel, [cancel]);

  return { supported, speaking, speak, cancel, voiceName: frenchVoice?.name };
}
